import { useEffect, useRef, useState } from 'react';
import { fetchStatistics } from '../services/supplierStatsService';

const STATISTICS_TIMEOUT_MS = 30_000;

/** Keyed results disappear during the very render in which filters change. */
export function useStatisticsQuery<T>(
  path: string,
  params: Record<string, string | number>,
  refresh: number,
) {
  const key = JSON.stringify([path, params, refresh]);
  const [result, setResult] = useState<{
    key: string;
    data?: T;
    error?: string;
  } | null>(null);
  const generation = useRef(0);
  const previousSearch = useRef(params.search);
  useEffect(() => {
    const controller = new AbortController();
    const id = ++generation.current;
    const [, requestParams] = JSON.parse(key) as [
      string,
      Record<string, string | number>,
      number,
    ];
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const fetch = () => {
      timeout = setTimeout(() => {
        if (id !== generation.current || controller.signal.aborted) return;
        setResult({
          key,
          error:
            'Hämtningen tog för lång tid. Försök igen eller välj en kortare period.',
        });
        controller.abort();
      }, STATISTICS_TIMEOUT_MS);
      return fetchStatistics<T>(path, requestParams, controller.signal)
        .then((data) => {
          if (id === generation.current && !controller.signal.aborted)
            setResult({ key, data });
        })
        .catch((error) => {
          if (id === generation.current && !controller.signal.aborted)
            setResult({
              key,
              error:
                error?.response?.data?.message ||
                'Kunde inte hämta statistik för valda filter.',
            });
        })
        .finally(() => clearTimeout(timeout));
    };
    // Typing should issue one request for the final search, while other filters and refresh remain immediate.
    const searchChanged = previousSearch.current !== requestParams.search;
    previousSearch.current = requestParams.search;
    const timer = searchChanged ? setTimeout(fetch, 250) : undefined;
    if (!searchChanged) fetch();
    return () => {
      if (timer !== undefined) clearTimeout(timer);
      clearTimeout(timeout);
      controller.abort();
      generation.current++;
    };
  }, [key, path]);
  const current = result?.key === key ? result : null;
  return { data: current?.data, error: current?.error, loading: !current };
}
